import { Stack } from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { useCallback, useMemo, useState } from 'react';
import { MergeEntityCard, MergeTargetPicker } from 'src/components/MergeTargetPicker';
import { ModalContainer } from 'src/components/ModalContainer';
import { Client, useMergeClient } from 'src/graphql';
import { useCustomViews } from 'src/graphql/entities/custom-view';
import { EntityNames } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { CLIENT_ICON, toClientSummary } from './client-summary';
import { useClientTargetSearch } from './use-client-target-search';

export const MergeClientModal = () => {
  const { closeModal } = useModalMethods(Modals.MergeClientModal);
  const { isOpened, modalProps } = useModalState(Modals.MergeClientModal);
  const [clientTarget, setClientTarget] = useState<Client | null>(null);
  const { mergeClient } = useMergeClient();
  const client = modalProps?.client;

  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const {
    records,
    loading: searchLoading,
    hasMore,
    loadMore,
    setSearchTerm,
  } = useClientTargetSearch({
    partnerId: client?.partnerId ?? selectedPartnerId,
    excludedClientId: client?.clientId,
  });

  const closeMergeModal = useCallback(() => {
    closeModal();
    setClientTarget(null);
    setSearchTerm('');
  }, [closeModal, setSearchTerm]);

  const { customViewId } = useRouteParams(UrlParams.customViewId);

  const { customViews } = useCustomViews({
    entity: EntityNames.Client,
    partnerId: selectedPartnerId,
  });

  const customView = useMemo(
    () => customViews.find((i) => i.customViewId === customViewId),
    [customViews, customViewId],
  );

  const { navigate } = useNavigateRoute();

  const handleMergeClients = useCallback(async () => {
    if (!clientTarget?.clientId) {
      showErrorAlert(`Invalid client target ${clientTarget?.clientId}`);

      return;
    }

    if (!client?.clientId) {
      showErrorAlert(`Invalid client origin ${client?.clientId}`);

      return;
    }

    closeMergeModal();

    const response = await mergeClient({
      clientTarget,
      clientOrigin: client,
    });

    if (!response) {
      return;
    }

    if (response.error) {
      console.error(response.error);
      showErrorAlert(response.error);
      return;
    }

    showSuccessAlert('Clients were merged');
    setClientTarget(null);

    if (customView?.entityName === EntityNames.Client) {
      navigate(
        portalPaths.entity.selected(
          customView.entityName,
          customViewId,
          clientTarget?.clientId,
        ),
      );
    }
  }, [
    client,
    clientTarget,
    closeMergeModal,
    mergeClient,
    showSuccessAlert,
    customView?.entityName,
    showErrorAlert,
    navigate,
    customViewId,
  ]);

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Merge clients"
      onClose={closeMergeModal}
      actions={[
        { label: 'Cancel', kind: 'secondary' },
        {
          label: 'Merge clients',
          kind: 'primary',
          onClick: handleMergeClients,
          disabled: !client || !clientTarget,
          testId: 'merge-client-submit',
        },
      ]}
      data-testid="merge-client-modal"
    >
      <Stack gap={2}>
        {client ? (
          <MergeEntityCard
            label="From"
            icon={CLIENT_ICON}
            summary={toClientSummary(client)}
            testId="merge-origin-card"
          />
        ) : null}
        <MergeTargetPicker
          label="Into"
          placeholder="Search clients"
          icon={CLIENT_ICON}
          records={records}
          target={clientTarget}
          toSummary={toClientSummary}
          onTargetChange={setClientTarget}
          onSearchChange={setSearchTerm}
          loading={searchLoading}
          hasMore={hasMore}
          loadMore={loadMore}
        />
      </Stack>
    </ModalContainer>
  );
};
